import { useEffect, useRef, useState } from "react";
import Pagination from "../../common/Pagination/index.js";
import { MdDeleteForever } from "react-icons/md";
import { BiSolidEditAlt } from "react-icons/bi";
import Loader from "../../common/Loader/index.js";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance.js";
import { RiFilter2Fill } from "react-icons/ri";
import { notify, Toast } from "../../common/Toast/index.js";
import Model from "../../common/Model/index.js";
import formatDateIndian from "../../common/formatDateIndian.js";
import { getUserMenuPermission } from "../../common/Helper/index.js";
import { FaRegEye } from "react-icons/fa";
import Filter from "../../common/Filter/index.js";

const Sitedetails = () => {
  const location = useLocation();
  const passedData = location.state?.siteData || [];
  const isFilteredFromDashboard =
    location.state?.isFilteredFromDashboard || false;
  const totalPassedRecords = location.state?.totalRecords || 0;
  const permission = getUserMenuPermission(7);
  const [isSiteData, setIsSiteData] = useState([]);
  const navigate = useNavigate();
  const [salePersons, setSalePersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isModal, setIsModal] = useState(false);
  const filterBtnRef = useRef(null);
  const [deleteSiteId, setDeleteSiteId] = useState(null);
  const [filters, setFilters] = useState({
    siteName: "",
    ownerName: "",
    salePerson: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    siteName: "",
    ownerName: "",
    salePerson: "",
  });
  const [initialized, setInitialized] = useState(false);
  const userId = sessionStorage.getItem("userId");

  useEffect(() => {
    if (!initialized) {
      if (isFilteredFromDashboard && passedData.length > 0) {
        setIsSiteData(passedData);
        // setTotalRecords(passedData.length);
        setLoading(false);
      } else {
        fetchSiteData();
      }
      setInitialized(true);
    }
  }, [initialized, isFilteredFromDashboard, passedData]);

  useEffect(() => {
    const fetchSalePersons = async () => {
      try {
        const res = await axiosInstance.get("User/GetAllUser");
        const salesData = res.data.map((person) => ({
          sale_Person_Id: Number(person.sale_Person_Id),
          Sale_Person_Name: person.Sale_Person_Name,
        }));

        setSalePersons(salesData);
      } catch (error) {
        notify("Error fetching sale persons:", error);
      }
    };

    fetchSalePersons();
  }, []);

  const fetchSiteData = async () => {
    try {
      setLoading(true);

      const hasFilters = Object.values(appliedFilters).some(
        (val) => val.trim() !== ""
      );

      const res = await axiosInstance.post("SiteDetail/GetAll", {
        pageNumber: currentPage,
        pageSize,
        site_Name: appliedFilters.siteName,
        owner_Name: appliedFilters.ownerName,
        sale_Person_Id: hasFilters
          ? appliedFilters.salePerson
            ? Number(appliedFilters.salePerson)
            : null
          : Number(userId),
      });

      setIsSiteData(res.data.deals);
      setTotalRecords(res.data.total_Records);
      setTotalPages(
        res.data.deals.length < pageSize ? currentPage : currentPage + 1
      );
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchSiteData();
  // }, [currentPage, appliedFilters]);

  useEffect(() => {
    if (!isFilteredFromDashboard) {
      fetchSiteData();
    }
  }, [currentPage, appliedFilters]);

  const handleEdit = async (siteId) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(
        `SiteDetail/GetDealById/${siteId}`
      );
      notify("Site details loaded for editing.", "success");
      navigate("/transaction/site-details", { state: { siteData: data } });
    } catch (error) {
      notify("Error fetching site details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (siteId) => {
    setDeleteSiteId(siteId);
    setIsModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteSiteId) return;

    try {
      await axiosInstance.delete(`SiteDetail/Delete/${deleteSiteId}`);
      setAppliedFilters({ ...appliedFilters });
      notify("SiteDetail deleted successfully!", "error");
    } catch (err) {
      notify("Error deleting site detail:", err);
    } finally {
      setIsModal(false);
      setDeleteSiteId(null);
    }
  };

  const applyFilters = () => {
    if (isFilteredFromDashboard) return;
    setAppliedFilters(filters);
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setFilters({ siteName: "", ownerName: "", salePerson: "" });
    setAppliedFilters({ siteName: "", ownerName: "", salePerson: "" });
  };

  const saleOptions = [
    ...salePersons.map((per) => ({
      value: per.sale_Person_Id?.toString() || "",
      label: per.Sale_Person_Name || "Unnamed Sales",
    })),
  ];

  const handleView = async (siteId) => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(
        `SiteDetail/GetDealById/${siteId}`
      );
      navigate("/transaction/siteview-details", { state: { siteData: data } });
    } catch (error) {
      notify("Error fetching site details", "error");
    } finally {
      setLoading(false);
    }
  };

  const filterFields = [
    {
      label: "Site Name",
      name: "siteName",
      type: "text",
      placeholder: "Search site name",
    },
    {
      label: "Sale Person Name",
      name: "salePerson",
      type: "select",
      options: saleOptions,
    },
    {
      label: "Owner Name",
      name: "ownerName",
      type: "text",
      placeholder: "Search Owner Name",
    },
  ];

  return (
    <>
      <Toast />
      <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5">
        <div className="relative flex flex-wrap justify-between items-center gap-4 mb-4">
          {permission.isInsert && (
            <button
              className="bg-primary rounded px-4 text-white py-2 w-full sm:w-auto"
              onClick={() => navigate("/transaction/site-details")}
            >
              + Add New Site
            </button>
          )}
          <button
            ref={filterBtnRef}
            className="flex items-center gap-2 bg-gray-200 px-3 py-2 rounded-md"
            onClick={() => setIsFilterOpen((prev) => !prev)}
          >
            <RiFilter2Fill className="text-lg text-primary" />
            Filter
          </button>
        </div>

        <Filter
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filterFields={filterFields}
          filters={filters}
          setFilters={setFilters}
          applyFilters={applyFilters}
          clearFilters={clearFilters}
        />

        {loading ? (
          <Loader />
        ) : permission.isView ? (
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full min-w-[700px] text-sm text-left border border-gray rounded-lg">
              <thead className="bg-primary text-white tracking-widest text-center font-light">
                <tr>
                  <th className="border border-gray-400 px-4 py-3">
                    Site Name
                  </th>
                  <th className="border border-gray-400 px-4 py-3">
                    Site Type
                  </th>
                  <th className="border border-gray-400 px-4 py-3">
                    Owner Name
                  </th>
                  <th className="border border-gray-400 px-4 py-3">
                    Sale Person
                  </th>
                  <th className="border border-gray-400 px-4 py-3">
                    Created Date
                  </th>
                  <th
                    className={`${
                      !permission.isUpdate && !permission.isDelete
                        ? "hidden"
                        : ""
                    } border border-gray-400 px-4 py-3 whitespace-nowrap`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="text-center">
                {isSiteData?.length > 0 ? (
                  isSiteData?.map((site) => (
                    <tr
                      key={site.site_Id}
                      className="border-b hover:bg-gray-50 transition cursor-pointer"
                    >
                      <td className="border border-gray-100 px-4 py-2">
                        {site.site_Name}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {site.site_Type}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {site.owner_Name}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {salePersons.find(
                          (sp) =>
                            Number(sp.sale_Person_Id) ===
                            Number(site.sale_Person_Id)
                        )?.Sale_Person_Name || "-"}
                      </td>

                      <td className="border border-gray-100 px-4 py-2">
                        {formatDateIndian(site.created_Date)}
                      </td>

                      {(permission.isUpdate || permission.isDelete) && (
                        <td className="py-3 border border-gray-100 text-center space-x-2">
                          <button
                            className="bg-gray-200 text-black-100 hover:bg-gray-400 px-2 py-1 rounded-md"
                            onClick={() => handleView(site.site_Id)}
                          >
                            <FaRegEye className="w-5 h-5" />
                          </button>
                          {permission.isDelete && (
                            <button
                              className="bg-red-100 text-red-600 hover:bg-red-200 px-2 py-1 rounded-md"
                              onClick={() => handleDelete(site.site_Id)}
                            >
                              <MdDeleteForever className="w-5 h-5" />
                            </button>
                          )}
                          {permission.isUpdate && (
                            <button
                              className="bg-[#e3f2fd] text-blue-600 hover:bg-blue-200 px-2 py-1 rounded-md"
                              onClick={() => handleEdit(site.site_Id)}
                            >
                              <BiSolidEditAlt className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-red-500 text-lg py-10">
            You don't have permission to view this page.
          </div>
        )}
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-6">
            Showing {isSiteData.length + (currentPage - 1) * 10} of{" "}
            {totalRecords} records
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
      <Model isOpen={isModal} onClose={() => setIsModal(false)}>
        <div className="p-4 text-center">
          <p className="text-lg font-semibold mb-4">
            Are you sure you want to delete this site?
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-gray-5 px-4 py-2 rounded-md"
              onClick={() => setIsModal(false)}
            >
              Cancel
            </button>
            <button
              className="bg-danger text-white px-4 py-2 rounded-md"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default Sitedetails;
